import { ApiService } from "@core/api/api-service";
import { addResponseInterceptor, removeResponseInterceptor } from "@core/api/interceptors/interceptor";
import { fakeBadNetworkInterceptor } from "@core/debug/network-debug-interceptors";
import { storage } from "@core/storage/storage";
import { isNil, isNotNil } from "@utils/assert";
import { observable } from "micro-observables";

interface DebugSettings {
	enableRandomNetworkErrors: boolean;
}

const DEFAULT_DEBUG_SETTINGS: DebugSettings = {
	enableRandomNetworkErrors: false,
};

export class NetworkDebugManager {
	private _randomErrorsInterceptor: number | null = null;

	private readonly _debugSettings = observable(DEFAULT_DEBUG_SETTINGS);
	debugSettings = this._debugSettings.readOnly();

	private readonly _settingsStore = storage.getStore<DebugSettings, DebugSettings>(
		"debugSettings",
		s => s,
		s => s,
	);

	constructor(private readonly apiService: ApiService) {
		const storedSettings = this._settingsStore.getItem(DEFAULT_DEBUG_SETTINGS);
		this._debugSettings.set({ ...storedSettings });

		if (this.debugSettings.get().enableRandomNetworkErrors) {
			this.toggleRandomNetworkErrors(true);
		}
	}

	toggleRandomNetworkErrors(enable: boolean): void {
		if (enable) {
			this.enableRandomNetworkErrors();
			return;
		}
		this.disableRandomNetworkErrors();
	}

	private enableRandomNetworkErrors(): void {
		if (isNil(this._randomErrorsInterceptor)) {
			this._randomErrorsInterceptor = addResponseInterceptor(
				this.apiService.axiosInstance,
				fakeBadNetworkInterceptor(),
			);
		}
		this.updateRandomNetworkErrorsSetting(true);
	}

	private disableRandomNetworkErrors(): void {
		if (isNotNil(this._randomErrorsInterceptor)) {
			removeResponseInterceptor(this.apiService.axiosInstance, this._randomErrorsInterceptor);
			this._randomErrorsInterceptor = null;
		}
		this.updateRandomNetworkErrorsSetting(false);
	}

	private updateRandomNetworkErrorsSetting(enableRandomNetworkErrors: boolean): void {
		this._debugSettings.update(previousSettings => ({
			...previousSettings,
			enableRandomNetworkErrors,
		}));
		this._settingsStore.setItem(this.debugSettings.get());
	}
}
